import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, RefreshCw, Calendar, Clock, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const SettingsBookingWindow = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [windowData, setWindowData] = useState({
        mode: 'rolling', // 'rolling' or 'fixed'
        min_days_ahead: 1,
        max_days_ahead: 60,
        fixed_start_date: null,
        fixed_end_date: null
    });

    const API_BASE_URL = '/api';
    const token = localStorage.getItem('admin_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    useEffect(() => {
        fetchSettings();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_BASE_URL}/admin/settings?id=global`, { headers });
            if (data.success && data.settings) {
                const w = data.settings.booking_window || {};
                setWindowData({
                    mode: w.mode || 'rolling',
                    min_days_ahead: w.min_days_ahead ?? 1,
                    max_days_ahead: w.max_days_ahead ?? 60,
                    fixed_start_date: w.fixed_start_date ? new Date(w.fixed_start_date) : null,
                    fixed_end_date: w.fixed_end_date ? new Date(w.fixed_end_date) : null
                });
            }
        } catch (error) {
            console.error('Error fetching booking window settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            // Get current global settings to preserve other fields
            const { data: currentData } = await axios.get(`${API_BASE_URL}/admin/settings?id=global`, { headers });
            const currentSettings = currentData.settings || {};

            const updatedSettings = {
                ...currentSettings,
                booking_window: windowData
            };

            await axios.put(`${API_BASE_URL}/admin/settings`, updatedSettings, { headers });
            toast.success('Booking window updated!');
        } catch (error) {
            console.error('Error saving booking window:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-4 text-gray-500">Loading settings...</div>;

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-8 py-6 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
                <div className="p-3 bg-[#471F21]/5 text-[#471F21] rounded-xl">
                    <Calendar className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Booking Window</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Define your operational horizon for photoshoot bookings.</p>
                </div>
            </div>

            <div className="p-8">
                {/* Window Mode Toggle */}
                <div className="mb-10 p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                        WINDOW TYPE
                    </label>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setWindowData({ ...windowData, mode: 'rolling' })}
                            className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 transition-all font-bold ${windowData.mode === 'rolling'
                                    ? 'bg-[#471F21] border-[#471F21] text-white shadow-lg'
                                    : 'bg-white border-gray-100 text-gray-500 hover:border-gray-200'
                                }`}
                        >
                            <Globe className="w-5 h-5" />
                            Rolling Days
                        </button>
                        <button
                            onClick={() => setWindowData({ ...windowData, mode: 'fixed' })}
                            className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 transition-all font-bold ${windowData.mode === 'fixed'
                                    ? 'bg-[#471F21] border-[#471F21] text-white shadow-lg'
                                    : 'bg-white border-gray-100 text-gray-500 hover:border-gray-200'
                                }`}
                        >
                            <Clock className="w-5 h-5" />
                            Fixed Dates
                        </button>
                    </div>
                </div>

                {windowData.mode === 'rolling' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                                EARLIEST BOOKING (DAYS FROM TODAY)
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="0"
                                    value={windowData.min_days_ahead}
                                    onChange={(e) => setWindowData({ ...windowData, min_days_ahead: parseInt(e.target.value) || 0 })}
                                    className="w-full px-5 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
                                />
                                <div className="mt-2 flex items-start gap-1.5 px-1">
                                    <div className="w-1 h-1 rounded-full bg-gray-300 mt-1.5"></div>
                                    <p className="text-[10px] text-gray-400 font-medium leading-relaxed">Set to 0 for same-day bookings, 1 for next-day onwards.</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                                LATEST BOOKING (DAYS FROM TODAY)
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="1"
                                    value={windowData.max_days_ahead}
                                    onChange={(e) => setWindowData({ ...windowData, max_days_ahead: parseInt(e.target.value) || 1 })}
                                    className="w-full px-5 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
                                />
                                <div className="mt-2 flex items-start gap-1.5 px-1">
                                    <div className="w-1 h-1 rounded-full bg-gray-300 mt-1.5"></div>
                                    <p className="text-[10px] text-gray-400 font-medium leading-relaxed">Maximum future date users can see in the calendar.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                                START DATE
                            </label>
                            <DatePicker
                                selected={windowData.fixed_start_date}
                                onChange={(date) => setWindowData({ ...windowData, fixed_start_date: date })}
                                dateFormat="MMMM d, yyyy"
                                className="w-full px-5 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
                                placeholderText="Select start date"
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                                END DATE
                            </label>
                            <DatePicker
                                selected={windowData.fixed_end_date}
                                onChange={(date) => setWindowData({ ...windowData, fixed_end_date: date })}
                                dateFormat="MMMM d, yyyy"
                                minDate={windowData.fixed_start_date}
                                className="w-full px-5 py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-800 focus:border-[#471F21] focus:ring-0 outline-none transition-all shadow-sm"
                                placeholderText="Select end date"
                            />
                        </div>
                    </div>
                )}

                <div className="flex justify-end p-2 border-t border-gray-50 pt-8 mt-2">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-3 px-10 py-3.5 bg-[#471F21] text-white font-bold rounded-xl hover:shadow-xl hover:shadow-[#471F21]/20 disabled:opacity-50 transition-all transform active:scale-95"
                    >
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Apply Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettingsBookingWindow;
